import React, { useContext, useEffect, useState } from "react";
import useScopesGraph, { type ScopesMap } from "./useScopesGraph";
import { getRenderableScopeChildren } from "./scopesGraphUtils";
import {
  ScopesContext,
  type SelectedScope as ScopeProviderSelectedScope,
} from "../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import { resourceTypes } from "../../../Findings/FindingsUtils";
import "./ScopesGraph.scss";
import ScopeFindingsBySeverity from "./ScopeFindingsBySeverity";
import FindingsByTicketStatus from "./FindingsByTicketStatus";
import { getIconByType } from "../../../Components/Tree/TreeUtils";
import TicketDueDate from "./TicketDueDate";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../General/useSeverityAsPriority";
import { capitalize } from "lodash-es";
import WidgetError from "../WidgetError/WidgetError";
import { AppPreferencesContext } from "../../../General/AppPreferencesProvider";
import CriticalCountCell from "./CriticalCountCell";
import useSelectScope, {
  findScopeSelectionNode,
} from "../../../AppView/ScopeManagement/useSelectScope";
import {
  Tooltip,
  SkeletonLoader,
  isUndefinedOrNull,
  normalizeNumbers,
  formatNumber,
  Loader,
} from "@SeemplicityDev/shared";
import type { SelectedScope } from "../../../types/scope";

// `resourceTypes` comes from the still-JS FindingsUtils module; type the
// constants we read so the comparisons below stay checked. Drop this once
// FindingsUtils is migrated to TS and exports its own types.
const resourceTypesTyped = resourceTypes as { SCOPE: string; GROUP: string };

const SWITCH_TO_SCOPE_TOOLTIP = "Switch to scope";
const SWITCH_TO_GROUP_TOOLTIP = "Switch to group";
const SCOPE_ALREADY_SELECTED_TOOLTIP = "Scope is already selected";
const EMPTY_SCOPE_GROUP_SUBTITLE =
  "This scope group is empty. Assign a scope to it, or delete it.";

export interface ScopesGraphProps {
  filterToExecute?: string;
  selectedScope?: SelectedScope | null;
  scopeToExecute?: string | null;
  id: string;
}

const ScopesGraph: React.FC<ScopesGraphProps> = ({
  filterToExecute,
  selectedScope,
  scopeToExecute,
  id,
}) => {
  const { currentSeverityMapping, getSeverityField } = useSeverityAsPriority();

  const {
    appPreferences: { dueDateTicketSlaEnabled },
  } = useContext(AppPreferencesContext);

  const {
    getScopeValueById,
    scopesState: { scopesList },
  } = useContext(ScopesContext);
  // The widget renders outside `ScopeManagementContext`, so opt into persistence
  // explicitly — otherwise the next scope-list refetch reverts the drilldown.
  const { selectScope, isScopeSelected } = useSelectScope({
    persistSelection: true,
  });
  const [scopesChildren, setScopesChildren] = useState<(string | undefined)[]>(
    []
  );
  const [hasRenderableScopes, setHasRenderableScopes] = useState(true);

  const onScopeNameClick = (scopeId?: string) => {
    const node = findScopeSelectionNode(scopesList?.[0], scopeId);
    if (!node) {
      return;
    }

    selectScope(node);
  };

  const {
    setScopesMap,
    resourceToScopeMap,
    scopesMap,
    maxNumOfTickets,
    maxNumOfFindings,
    setMaxNumOfTickets,
    refetch,
    finalError: error,
    refetching,
  } = useScopesGraph({
    scopes: scopesChildren,
    filterToExecute,
  });

  useEffect(() => {
    if (!!selectedScope?.value?.id && !!filterToExecute) {
      if (!selectedScope?.value?.id && !scopeToExecute) {
        return;
      }
      const currentScope = getScopeValueById(
        // Guarded by the `!!selectedScope?.value?.id` check above.
        selectedScope?.value?.id as string,
        scopesList?.[0]
      );
      let scopeChildren: ScopeProviderSelectedScope[] | undefined = [];
      if (currentScope?.value?.type === resourceTypesTyped.SCOPE) {
        scopeChildren = [
          {
            ...currentScope,
            label: currentScope?.label,
            value: { ...currentScope?.value },
          },
        ];
      } else {
        scopeChildren = getRenderableScopeChildren(
          currentScope?.value?.children?.filter(
            (scope) => scope?.value?.type !== resourceTypesTyped.SCOPE
          )
        );

        if (!scopeChildren?.length) {
          scopeChildren = currentScope?.value?.children?.filter(
            (scope) => scope?.value?.type === resourceTypesTyped.SCOPE
          );
        }
      }

      setHasRenderableScopes(!!scopeChildren?.length);

      const scopesMapCopy: ScopesMap = new Map();
      scopeChildren?.forEach((scope) => {
        scopesMapCopy.set(scope?.value?.id, {
          name: scope?.label,
          id: scope?.value?.id,
          type: scope?.value?.type,
          isLoading: true,
        });
      });

      setScopesMap(scopesMapCopy);
      // Original passes the mapped result through directly (possibly undefined
      // when `scopeChildren` is undefined); cast keeps that exact behavior.
      setScopesChildren(
        scopeChildren?.map((scope) => scope?.value?.id) as (
          | string
          | undefined
        )[]
      );
    }
  }, [selectedScope?.value?.id, filterToExecute, refetching, scopesList]);

  return (
    <>
      {!selectedScope?.value?.id ? (
        <Loader
          data-testid={"scopes-graph-no-scope-permission"}
          className={"scopes-graph-no-scope-permission"}
          center
          noBackground
          icon={"seem-icon-warning"}
          subtitle={
            "This widget requires a saved scope or scope group to display data."
          }
        />
      ) : !error ? (
        <div
          className={`scopes-graph-widget ${
            dueDateTicketSlaEnabled ? "has-due-date-col" : ""
          }`}
        >
          {!hasRenderableScopes ? (
            <Loader
              data-testid={"scopes-graph-no-data"}
              className={"empty-state-no-match-to-filter"}
              center
              noBackground
              icon={"seem-icon-manualfinding"}
              subtitle={EMPTY_SCOPE_GROUP_SUBTITLE}
            />
          ) : (
            <div className="scopes-graph-body-inner">
              <div className="scopes-graph-titles-wrap">
                <div className="scope-graph-col-1">
                  <div className="scopes-graph-title" title={"Scope"}>
                    Scope
                  </div>
                </div>
                <div className="scope-graph-col-2">
                  <div
                    className="scopes-graph-title ratio-title-wrap"
                    title={`${capitalize(
                      getSeverityField(3, SeverityFieldsEnum.NAME)
                    )} Finding Ratio`}
                  >
                    <span className={"ratio-title"}>
                      {`${capitalize(
                        getSeverityField(3, SeverityFieldsEnum.NAME)
                      )} Finding Ratio`}
                    </span>
                    <Tooltip
                      type="info"
                      content={`Average number of ${capitalize(
                        getSeverityField(3, SeverityFieldsEnum.NAME)
                      )} Findings per Resource, across all Resources in the current scope/group`}
                    >
                      <i className={"seem-icon-help"} />
                    </Tooltip>
                  </div>
                </div>
                <div className="scope-graph-col-3">
                  <div className="scopes-graph-title" title={"Findings"}>
                    Findings
                  </div>
                </div>
                <div className="scope-graph-col-4">
                  <div className="scopes-graph-title" title={"Tickets"}>
                    Tickets
                  </div>
                </div>
                {dueDateTicketSlaEnabled && (
                  <div className="scope-graph-col-5">
                    <div
                      className="scopes-graph-title"
                      title={"Past Due Tickets"}
                    >
                      {"Past Due Tickets"}
                    </div>
                  </div>
                )}
              </div>
              <div className={"scopes-graphs-wrap"}>
                {!!scopesMap.size
                  ? Array.from(scopesMap?.keys())?.map((k) => {
                      // Keys come from `scopesMap` itself, so `get` is always
                      // populated here — assert to match the original access.
                      const dataItem = scopesMap.get(k)!;
                      const isAlreadySelected = isScopeSelected(dataItem.id);
                      const drilldownAction = isAlreadySelected
                        ? SCOPE_ALREADY_SELECTED_TOOLTIP
                        : dataItem.type === resourceTypesTyped.GROUP
                          ? SWITCH_TO_GROUP_TOOLTIP
                          : SWITCH_TO_SCOPE_TOOLTIP;
                      const resourceCount = resourceToScopeMap?.[k as string];
                      const ratio =
                        resourceCount > 0
                          ? (dataItem.scopeData
                              ?.count_per_severity?.[3] as number) /
                            resourceCount
                          : 0;
                      return (
                        <div
                          key={`scope-${dataItem.id}`}
                          className={`scopes-graph-row ${
                            dataItem.isLoading ? "is-loading" : ""
                          }`}
                        >
                          <div className="scope-graph-col-1">
                            {dataItem.isLoading ? (
                              <SkeletonLoader />
                            ) : (
                              <Tooltip
                                type="info"
                                content={
                                  <>
                                    <div>{drilldownAction}</div>
                                    {/* Replaces the native `title` this tooltip
                                      would otherwise compete with. */}
                                    <div>{dataItem.name}</div>
                                  </>
                                }
                              >
                                <div
                                  className={`scopes-graph-item-wrap ${
                                    isAlreadySelected ? "" : "is-clickable"
                                  }`}
                                  role={
                                    isAlreadySelected ? undefined : "button"
                                  }
                                  onClick={
                                    isAlreadySelected
                                      ? undefined
                                      : () => onScopeNameClick(dataItem.id)
                                  }
                                >
                                  <i
                                    className={`seem-icon ${getIconByType(
                                      dataItem.type as string
                                    )}`}
                                  />
                                  <div className="scopes-graph-item-name">
                                    {dataItem.name}
                                  </div>
                                </div>
                              </Tooltip>
                            )}
                          </div>
                          <div
                            className={`scope-graph-col-2 ${
                              dataItem.isLoading ? "is-loading" : ""
                            }`}
                          >
                            <div
                              className={`scopes-graph-critical-count ${
                                ratio === 0 ? "is-disabled" : ""
                              }`}
                            >
                              {dataItem.isLoading ||
                              isUndefinedOrNull(resourceCount) ? (
                                <SkeletonLoader />
                              ) : (
                                <div className="scopes-graph-col-critical-count-wrap">
                                  <Tooltip
                                    type="info"
                                    content={` ${formatNumber(
                                      dataItem.scopeData
                                        ?.count_per_severity?.[3]
                                    )} ${capitalize(
                                      getSeverityField(
                                        3,
                                        SeverityFieldsEnum.NAME
                                      )
                                    )} Findings / ${formatNumber(
                                      resourceCount
                                    )} Resources`}
                                  >
                                    <div className="scopes-graph-col-critical-count">
                                      {ratio > 0
                                        ? parseFloat(ratio.toFixed(3)) === 0
                                          ? "0"
                                          : ratio.toFixed(3)
                                        : 0}
                                    </div>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`scope-graph-col-3`}>
                            <div className="scope-graph-col-3-inner">
                              {dataItem.isLoading ? (
                                <SkeletonLoader width={"100%"} />
                              ) : (
                                <CriticalCountCell
                                  currentScope={dataItem?.id}
                                  filterToExecute={filterToExecute}
                                  criticalCount={
                                    dataItem.scopeData?.count_per_severity?.[3]
                                  }
                                  totalCount={dataItem.scopeData?.total_count}
                                />
                              )}
                            </div>
                            <div className={"scope-graph-col-3-inner"}>
                              <ScopeFindingsBySeverity
                                findingCounts={
                                  dataItem.scopeData?.count_per_severity
                                }
                                totalCount={dataItem.scopeData?.total_count}
                                maxFindings={maxNumOfFindings || 100}
                                scopeId={k}
                                loading={dataItem.isLoading}
                                widgetId={id}
                                isForExport={false}
                                filterToExecute={filterToExecute}
                              />
                            </div>
                          </div>
                          <div className={`scope-graph-col-4`}>
                            <div className="scope-graph-col-4-inner">
                              {dataItem.isLoading ? (
                                <SkeletonLoader />
                              ) : (
                                <div
                                  className={
                                    "scopes-graph-col-tickets-count-wrap"
                                  }
                                >
                                  <div
                                    className={`scopes-graph-col-tickets-count ${
                                      dataItem.scopeData?.total_tickets === 0
                                        ? "is-disabled"
                                        : ""
                                    }`}
                                  >
                                    {normalizeNumbers(
                                      dataItem.scopeData?.total_tickets!
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className={"scope-graph-col-4-inner"}>
                              <FindingsByTicketStatus
                                scopeData={
                                  dataItem.scopeData?.per_ticket_status
                                }
                                max={maxNumOfTickets || 100}
                                scopeId={k}
                                totalCount={dataItem.scopeData?.total_tickets}
                                loading={dataItem.isLoading}
                                setMax={setMaxNumOfTickets}
                                widgetId={id}
                                filterToExecute={filterToExecute}
                              />
                            </div>
                          </div>
                          {!!dueDateTicketSlaEnabled && (
                            <div className={`scope-graph-col-5`}>
                              <div className="scope-graph-col-5-inner">
                                {dataItem.isLoading ? (
                                  <SkeletonLoader />
                                ) : (
                                  <div
                                    className={
                                      "scopes-graph-col-tickets-count-wrap"
                                    }
                                  >
                                    <div
                                      className={`scopes-graph-col-tickets-count ${
                                        dataItem.pastDueCount === 0
                                          ? "is-disabled"
                                          : ""
                                      }`}
                                    >
                                      {normalizeNumbers(
                                        dataItem?.pastDueCount!
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className={"scope-graph-col-5-inner"}>
                                <TicketDueDate
                                  scopeData={
                                    dataItem.scopeData
                                      ?.per_ticket_due_date_status
                                  }
                                  max={maxNumOfTickets || 100}
                                  scopeId={k}
                                  // TicketDueDate types totalCount as required;
                                  // the original passes it through possibly
                                  // undefined. Cast preserves that exact value.
                                  totalCount={
                                    dataItem.scopeData?.total_tickets as number
                                  }
                                  loading={dataItem.isLoading}
                                  setMax={setMaxNumOfTickets}
                                  widgetId={id}
                                  filterToExecute={filterToExecute}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  : Array.from({ length: scopesChildren?.length || 1 }).map(
                      (_, index) => (
                        <div
                          key={`scope-loader-${index}`}
                          className={`scopes-graph-row is-loading`}
                        >
                          <div className="scope-graph-col-1">
                            <SkeletonLoader />
                          </div>
                          <div className={`scope-graph-col-2 is-loading`}>
                            <SkeletonLoader />
                          </div>
                          <div className={`scope-graph-col-3`}>
                            <div className="scope-graph-col-3-inner">
                              <SkeletonLoader width={"100%"} />
                            </div>
                            <div className={"scope-graph-col-3-inner"}>
                              <SkeletonLoader width={"100%"} />
                            </div>
                          </div>
                          <div className={`scope-graph-col-4`}>
                            <div className="scope-graph-col-4-inner">
                              <SkeletonLoader />
                            </div>
                            <div className={"scope-graph-col-4-inner"}>
                              <SkeletonLoader width={"100%"} />
                            </div>
                          </div>
                          {!!dueDateTicketSlaEnabled && (
                            <div className={`scope-graph-col-5`}>
                              <div className="scope-graph-col-5-inner">
                                <SkeletonLoader />
                              </div>
                              <div className={"scope-graph-col-5-inner"}>
                                <SkeletonLoader width={"100%"} />
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
              </div>
              <div className="scopes-graph-widget-legend-container">
                <div className="scopes-graph-legend-col"></div>
                <div className="scopes-graph-legend-col"></div>
                <div className="scopes-graph-legend-col">
                  {currentSeverityMapping?.map((severity) => (
                    <div
                      className="scopes-legend-item-wrap"
                      key={`severity-${severity.name}`}
                    >
                      <div
                        className="scopes-legend-item-icon"
                        style={{
                          backgroundColor: severity?.color,
                        }}
                      ></div>
                      <div className="scopes-legend-item-text">
                        {capitalize(severity.name)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="scopes-graph-legend-col">
                  <div className="scopes-legend-item-wrap">
                    <div className="scopes-legend-item-icon scopes-legend-item-icon-backlog"></div>
                    <div className="scopes-legend-item-text">Backlog</div>
                  </div>
                  <div className="scopes-legend-item-wrap">
                    <div className="scopes-legend-item-icon scopes-legend-item-icon-scheduled"></div>
                    <div className="scopes-legend-item-text">Scheduled</div>
                  </div>
                  <div className="scopes-legend-item-wrap">
                    <div className="scopes-legend-item-icon scopes-legend-item-icon-in-progress"></div>
                    <div className="scopes-legend-item-text">In Progress</div>
                  </div>
                  <div className="scopes-legend-item-wrap">
                    <div className="scopes-legend-item-icon scopes-legend-item-icon-rejected"></div>
                    <div className="scopes-legend-item-text">Rejected</div>
                  </div>
                  <div className="scopes-legend-item-wrap">
                    <div className="scopes-legend-item-icon scopes-legend-item-icon-done"></div>
                    <div className="scopes-legend-item-text">Done</div>
                  </div>
                </div>
                {!!dueDateTicketSlaEnabled && (
                  <div className="scopes-graph-legend-col">
                    <div className="scopes-legend-item-wrap">
                      <div className="scopes-legend-item-icon scopes-legend-item-icon-upcoming"></div>
                      <div className="scopes-legend-item-text">Upcoming</div>
                    </div>
                    <div className="scopes-legend-item-wrap">
                      <div className="scopes-legend-item-icon scopes-legend-item-icon-past-due"></div>
                      <div className="scopes-legend-item-text">Past Due</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <WidgetError onWidgetsReload={refetch} />
      )}
    </>
  );
};

export default ScopesGraph;
